import { Injectable } from "@angular/core";
import { ApiMissionService } from "./api/api-mission.service";
import { AuthService } from "./auth.service";
import { NetworkService } from "./network.service";
import { PdvCacheService } from "./pdv-cache.service";
import { Pdv } from "../interfaces/pdv";

/**
 * Préchargement en tâche de fond des listes de PDV hors fichier (FR-179).
 *
 * Retour de recette : après chargement de tous les PDV, l'ouverture de
 * pdv-select (« + ») imposait un loader de plusieurs secondes. La consigne
 * client : la liste ne s'actualise en tâche de fond qu'à l'ouverture de
 * l'appli, le reste de la session sert le cache — transparent pour
 * l'utilisateur.
 *
 * Déclenché une fois par session (ouverture avec session existante, ou juste
 * après le login) : charge les campagnes hors fichier puis, séquentiellement
 * pour ne pas saturer l'API, la liste complète des PDV de chacune, déposée
 * dans PdvCacheService via `obtenir` (une ouverture simultanée de pdv-select
 * partage la requête en vol au lieu d'en émettre une deuxième). pdv-select
 * est déjà cache-first : si le préchargement n'est pas terminé quand
 * l'utilisateur arrive, la page charge elle-même (le comportement d'avant),
 * et le pull-to-refresh reste la porte de sortie.
 *
 * Best-effort intégral : hors ligne on ne tente rien (et on retentera au
 * prochain déclencheur), un échec global rend la main pour un nouvel essai,
 * une erreur par campagne est avalée — perdre l'accélérateur ne doit jamais
 * gêner la session.
 */
@Injectable({
  providedIn: "root",
})
export class PdvPreloadService {
  /** Une seule exécution par session ; réarmé au logout via `reset`. */
  private prechargement: Promise<void> | null = null;

  /**
   * Génération de session : incrémentée au logout. La boucle de préchargement
   * encore en cours s'arrête au lieu de continuer à remplir le cache pour le
   * compte suivant (appareil partagé).
   */
  private generation = 0;

  constructor(
    private apiMissionService: ApiMissionService,
    private authService: AuthService,
    private networkService: NetworkService,
    private pdvCache: PdvCacheService
  ) {
    // Session démarrée hors ligne, ou réseau revenu après un échec : réarmer
    // le préchargement au retour en ligne — sans cela, le cache restait vide
    // pour toute la session (review Greptile PR #308). Gardé par la session :
    // pas de requête condamnée depuis l'écran de connexion.
    this.networkService.statusChanged.subscribe((isOnline) => {
      if (isOnline) {
        void this.authService.loggedIn().then((connecte) => {
          if (connecte) {
            this.precharger();
          }
        });
      }
    });
  }

  /** Lance le préchargement s'il n'a pas déjà eu lieu cette session. */
  precharger(): void {
    if (!this.networkService.isOnline) {
      // Pas de mémoïsation hors ligne : le prochain déclencheur retentera.
      return;
    }
    this.prechargement ??= this.prechargerHorsFichier(this.generation).catch(
      () => {
        // Échec global (liste des campagnes inaccessible) : rendre la main
        // pour qu'un prochain déclencheur puisse retenter.
        this.prechargement = null;
      }
    );
  }

  /** À appeler au logout : le compte suivant doit précharger ses propres PDV. */
  reset(): void {
    this.generation++;
    this.prechargement = null;
  }

  private async prechargerHorsFichier(generation: number): Promise<void> {
    const campagnes = ((await this.apiMissionService.getListCampagnes(1)) ??
      []) as Array<{ id: string }>;

    for (const campagne of campagnes) {
      if (generation !== this.generation) {
        // Logout pendant le préchargement : ne pas remplir le cache du
        // compte suivant.
        return;
      }
      if (!campagne?.id) {
        continue;
      }
      try {
        await this.pdvCache.obtenir(
          campagne.id,
          true,
          () =>
            this.apiMissionService.getListPdv(
              campagne.id,
              true
            ) as Promise<Pdv[]>
        );
      } catch {
        // Campagne suivante : la page chargera elle-même si besoin.
      }
    }
  }
}
